import React, { useMemo, useState } from 'react';
import {
  DescriptionText,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  Surface,
  VerticalStack,
  WizardWorkspace,
  type CommerceSelectOptionViewModel,
} from '@workspace/silk-ui';
import { Cable, CheckCircle2, GitBranch, Settings2, Users } from 'lucide-react';

export interface NotificationRouteEditorFormState {
  id?: string;
  event_type: string;
  notification_type: string;
  channel_id: string;
  enabled: boolean;
  priority: string;
  quote_kind: string;
  status: string;
  previous_status: string;
  workflow_stage: string;
  severity: string;
  rule_key: string;
  resource_kind: string;
  site_id: string;
  device_role: string;
  recipient_group_ids: string[];
  include_customer_email: boolean;
}

interface NotificationRouteEditorWizardProps {
  isOpen: boolean;
  form: NotificationRouteEditorFormState;
  setForm: React.Dispatch<React.SetStateAction<NotificationRouteEditorFormState>>;
  channelOptions: CommerceSelectOptionViewModel[];
  recipientGroupOptions: CommerceSelectOptionViewModel[];
  eventOptions: Array<{ label: string; event_type: string; notification_type: string }>;
  saving: boolean;
  onSave: () => Promise<void>;
  onClose: () => void;
}

const inputCls =
  'h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

function eventOptionValue(option: { event_type: string; notification_type: string }): string {
  return `${option.event_type}::${option.notification_type}`;
}

function FormField({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
        {label}
      </label>
      {children}
      {hint ? <p className="text-[10px] font-medium text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function NotificationRouteEditorWizard({
  isOpen,
  form,
  setForm,
  channelOptions,
  recipientGroupOptions,
  eventOptions,
  saving,
  onSave,
  onClose,
}: NotificationRouteEditorWizardProps) {
  const [activeTabId, setActiveTabId] = useState('event');
  const mode = form.id ? 'edit' : 'create';
	const isStatusEvent = form.event_type.startsWith('status.alert.');
  const selectedEvent = useMemo(
    () =>
      eventOptions.find(
        option => option.event_type === form.event_type && option.notification_type === form.notification_type,
      ) ??
      eventOptions.find(option => option.event_type === form.event_type) ??
      eventOptions[0],
    [eventOptions, form.event_type, form.notification_type],
  );
  const selectedChannel = useMemo(
    () => channelOptions.find(option => option.value === form.channel_id) ?? null,
    [channelOptions, form.channel_id],
  );

  const summaryItems = useMemo(
    () => [
      { label: 'Event', value: selectedEvent?.label || 'Choose event', complete: Boolean(form.event_type) },
      { label: 'Channel', value: selectedChannel?.label || 'Choose channel', complete: Boolean(form.channel_id) },
      {
        label: 'Targets',
        value: [
          form.include_customer_email ? 'Customer email' : null,
          ...recipientGroupOptions.filter(option => form.recipient_group_ids.includes(option.value)).map(option => option.label),
        ].filter(Boolean).join(', ') || 'Choose targets',
        complete: form.include_customer_email || form.recipient_group_ids.length > 0,
      },
      { label: 'Priority', value: form.priority || '100', complete: Boolean(form.priority.trim()) },
      { label: 'Status', value: form.enabled ? 'Enabled' : 'Disabled', complete: true },
    ],
    [form.channel_id, form.enabled, form.priority, form.event_type, form.include_customer_email, form.recipient_group_ids, recipientGroupOptions, selectedChannel?.label, selectedEvent?.label],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <WizardWorkspace
      isOpen={true}
      title={mode === 'edit' ? 'Edit Route' : 'New Route'}
      subtitle="Map a supported event contract to a tenant delivery channel."
      tabs={[
        { id: 'event', label: 'Event', icon: <GitBranch className="h-4 w-4" /> },
        { id: 'delivery', label: 'Delivery', icon: <Cable className="h-4 w-4" /> },
        { id: 'filters', label: 'Filters', icon: <Users className="h-4 w-4" /> },
        { id: 'review', label: 'Review', icon: <CheckCircle2 className="h-4 w-4" /> },
      ]}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={mode === 'edit' ? 'Save Route' : 'Create Route'}
      summaryItems={summaryItems}
    >
        {activeTabId === 'event' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Event Mapping"
              subtitle="Choose the event contract that should produce a tenant delivery attempt."
            />
            <div className="px-6 pb-8">
              <PaneSection label="Event catalog" icon={GitBranch}>
                <FormField label="Supported event">
                  <select
                    aria-label="Event"
                    className={inputCls}
                    value={eventOptionValue(selectedEvent)}
                    onChange={(event) => {
                      const next = eventOptions.find(option => eventOptionValue(option) === event.target.value) ?? eventOptions[0];
                      setForm(current => ({
                        ...current,
                        event_type: next.event_type,
                        notification_type: next.notification_type,
						include_customer_email: next.notification_type === 'order_confirmation' || next.notification_type === 'quote_submitted',
						quote_kind: '',
						status: '',
						previous_status: '',
						workflow_stage: '',
						severity: '',
						rule_key: '',
						resource_kind: '',
						site_id: '',
						device_role: '',
                      }));
                    }}
                  >
                    {eventOptions.map(option => (
                      <option key={eventOptionValue(option)} value={eventOptionValue(option)}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>
              </PaneSection>
            </div>
          </VerticalStack>
        ) : null}

        {activeTabId === 'delivery' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Delivery Binding"
              subtitle="Bind the event to a tenant SMTP channel and priority."
            />
            <div className="px-6 pb-8">
              <PaneSection label="Channel binding" icon={Cable}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <FormField label="Channel">
                    <select
                      aria-label="Channel"
                      className={inputCls}
                      value={form.channel_id}
                      onChange={(event) => setForm(current => ({ ...current, channel_id: event.target.value }))}
                    >
                      <option value="">Select a channel</option>
                      {channelOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="Priority" hint="Lower numbers win first within the same scope.">
                    <input aria-label="Priority" className={inputCls} value={form.priority} onChange={(event) => setForm(current => ({ ...current, priority: event.target.value }))} />
                  </FormField>
                </div>
                <div className="mt-6">
                  <FormField label="Route status">
                    <label className="flex h-12 items-center gap-3 rounded-lg border border-border/60 bg-background px-4">
                      <input aria-label="Enabled" type="checkbox" checked={form.enabled} onChange={(event) => setForm(current => ({ ...current, enabled: event.target.checked }))} />
                      <span className="text-sm font-medium text-foreground">{form.enabled ? 'Enabled' : 'Disabled'}</span>
                    </label>
                  </FormField>
                </div>
                <div className="mt-6 space-y-4">
                  <FormField label="Recipients" hint="Routes can target reusable recipient groups and/or the customer email from the quote event.">
                    <div className="space-y-3 rounded-lg border border-border/60 bg-background p-4">
						{!isStatusEvent ? <label className="flex items-center gap-3 text-sm font-medium text-foreground">
                        <input
                          type="checkbox"
                          checked={form.include_customer_email}
                          onChange={event => setForm(current => ({ ...current, include_customer_email: event.target.checked }))}
                        />
                        <span>Customer email variable</span>
						</label> : null}
                      <div className="space-y-2">
                        {recipientGroupOptions.map(option => (
                          <label key={option.value} className="flex items-center gap-3 text-sm font-medium text-foreground">
                            <input
                              type="checkbox"
                              checked={form.recipient_group_ids.includes(option.value)}
                              onChange={event =>
                                setForm(current => ({
                                  ...current,
                                  recipient_group_ids: event.target.checked
                                    ? [...current.recipient_group_ids, option.value]
                                    : current.recipient_group_ids.filter(value => value !== option.value),
                                }))
                              }
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                        {recipientGroupOptions.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No recipient groups configured yet.</p>
                        ) : null}
                      </div>
                    </div>
                  </FormField>
                </div>
              </PaneSection>
            </div>
          </VerticalStack>
        ) : null}

        {activeTabId === 'filters' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Filters"
				subtitle={isStatusEvent ? 'Optionally restrict delivery using canonical status alert fields.' : 'Optionally restrict this route to specific quote kinds, statuses, or workflow stages.'}
            />
            <div className="px-6 pb-8">
			  <PaneSection label={isStatusEvent ? 'Status alert filters' : 'Quote filters'} icon={Settings2}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
				  {isStatusEvent ? <>
					<FormField label="Severity">
					  <input aria-label="Severity" className={inputCls} value={form.severity} onChange={(event) => setForm(current => ({ ...current, severity: event.target.value }))} />
					</FormField>
					<FormField label="Rule">
					  <input aria-label="Rule" className={inputCls} value={form.rule_key} onChange={(event) => setForm(current => ({ ...current, rule_key: event.target.value }))} />
					</FormField>
					<FormField label="Resource kind">
					  <input aria-label="Resource kind" className={inputCls} value={form.resource_kind} onChange={(event) => setForm(current => ({ ...current, resource_kind: event.target.value }))} />
					</FormField>
					<FormField label="Site">
					  <input aria-label="Site" className={inputCls} value={form.site_id} onChange={(event) => setForm(current => ({ ...current, site_id: event.target.value }))} />
					</FormField>
					<FormField label="Device role">
					  <input aria-label="Device role" className={inputCls} value={form.device_role} onChange={(event) => setForm(current => ({ ...current, device_role: event.target.value }))} />
					</FormField>
					<FormField label="State">
					  <input aria-label="State" className={inputCls} value={form.status} onChange={(event) => setForm(current => ({ ...current, status: event.target.value }))} />
					</FormField>
				  </> : <>
                  <FormField label="Quote kind">
                    <input aria-label="Quote kind" className={inputCls} value={form.quote_kind} onChange={(event) => setForm(current => ({ ...current, quote_kind: event.target.value }))} />
                  </FormField>
                  <FormField label="Status">
                    <input aria-label="Status" className={inputCls} value={form.status} onChange={(event) => setForm(current => ({ ...current, status: event.target.value }))} />
                  </FormField>
                  <FormField label="Previous status">
                    <input aria-label="Previous status" className={inputCls} value={form.previous_status} onChange={(event) => setForm(current => ({ ...current, previous_status: event.target.value }))} />
                  </FormField>
                  <FormField label="Workflow stage">
                    <input aria-label="Workflow stage" className={inputCls} value={form.workflow_stage} onChange={(event) => setForm(current => ({ ...current, workflow_stage: event.target.value }))} />
                  </FormField>
				  </>}
                </div>
              </PaneSection>
            </div>
          </VerticalStack>
        ) : null}

        {activeTabId === 'review' ? (
          <VerticalStack gap={6}>
            <PaneHeader
              title="Review"
              subtitle="Confirm the event-to-channel route before saving it for tenant delivery resolution."
            />
            <div className="px-6 pb-8">
              <PaneSection label="Route summary" icon={Settings2}>
                <Surface className="border-border/60 bg-muted/10 p-4">
                  <VerticalStack gap={3}>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Event</span>
                      <span className="font-bold text-foreground">{selectedEvent?.label || 'Unset'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Contract</span>
                      <span className="font-mono text-foreground">{form.event_type || 'not-set'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Channel</span>
                      <span className="font-bold text-foreground">{selectedChannel?.label || 'Unset'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Targets</span>
                      <span className="font-bold text-foreground">
                        {[
                          form.include_customer_email ? 'Customer email' : null,
                          ...recipientGroupOptions.filter(option => form.recipient_group_ids.includes(option.value)).map(option => option.label),
                        ].filter(Boolean).join(', ') || 'Unset'}
                      </span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Priority</span>
                      <span className="font-bold text-foreground">{form.priority || '100'}</span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Filters</span>
                      <span className="font-bold text-foreground">
						{(isStatusEvent
						  ? [form.severity, form.rule_key, form.resource_kind, form.site_id, form.device_role, form.status]
						  : [form.quote_kind, form.status, form.previous_status, form.workflow_stage]
						).filter(Boolean).join(' / ') || 'None'}
                      </span>
                    </HorizontalStack>
                    <HorizontalStack className="justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <span className="font-bold text-foreground">{form.enabled ? 'Enabled' : 'Disabled'}</span>
                    </HorizontalStack>
                  </VerticalStack>
                </Surface>
              </PaneSection>

              <PaneFooter className="mt-8">
                <DescriptionText>
                  Routes are tenant overrides. If you remove a route later, the platform fallback path may still apply depending on upstream notify policy.
                </DescriptionText>
              </PaneFooter>
            </div>
          </VerticalStack>
        ) : null}
    </WizardWorkspace>
  );
}
